// components/ui/dateTimePicker.jsx
import React, { useState, useEffect } from "react";
import { Calendar, Clock, X } from "lucide-react";

const DateTimePicker = ({ value, onChange, onClose, className = "" }) => {
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      const localDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      setSelectedDate(localDate.toISOString().split("T")[0]);
      setSelectedTime(localDate.toTimeString().split(" ")[0].substring(0, 5));
    }
  }, [value]);

  useEffect(() => {
    setIsValid(selectedDate && selectedTime);
  }, [selectedDate, selectedTime]);

  const handleSubmit = () => {
    if (isValid) {
      // Combine date and time in IST
      const dateTimeString = `${selectedDate}T${selectedTime}:00`;
      const localDateTime = new Date(dateTimeString);

      // Convert to UTC for API
      const utcDateTime = new Date(
        localDateTime.getTime() + localDateTime.getTimezoneOffset() * 60000
      );

      onChange(utcDateTime.toISOString());
      onClose();
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
    return localNow.toISOString().split("T")[0];
  };

  const getMinTime = () => {
    if (selectedDate === getMinDateTime()) {
      const now = new Date();
      const localNow = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000
      );
      return localNow.toTimeString().split(" ")[0].substring(0, 5);
    }
    return "00:00";
  };

  return (
    <div
      className={`absolute z-50 w-full mt-1 bg-white border border-input rounded-md shadow-lg p-4 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">
          Set Preferred Time
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            Date
          </label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            min={getMinDateTime()}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Clock className="h-4 w-4 inline mr-1" />
            Time (IST)
          </label>
          <input
            type="time"
            value={selectedTime}
            onChange={(e) => setSelectedTime(e.target.value)}
            min={selectedDate === getMinDateTime() ? getMinTime() : "00:00"}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
          />
        </div>

        {isValid && (
          <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
            <strong>Selected:</strong>{" "}
            {new Date(`${selectedDate}T${selectedTime}`).toLocaleString(
              "en-IN",
              {
                timeZone: "Asia/Kolkata",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid}
          className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-black/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Set Time
        </button>
      </div>
    </div>
  );
};

export { DateTimePicker };
